// src/pages/TaskBoard.jsx
import React, { useState, useMemo, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useTask } from "../context/TaskContext";
import Layout from "../components/common/Layout";
import { ADMIN_NAV, LEAD_NAV, EMPLOYEE_NAV } from "../utils/navigation";
import { normalizeStatus } from "../hooks/useFirestore";

const COLUMNS = [
  {
    id: "PENDING",
    label: "Todo",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        width="18"
        height="18"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z"
        />
      </svg>
    ),
    accent: "#6b7280",
    accentBg: "#f3f4f6",
    emptyMsg: "No tasks queued",
  },

  {
    id: "IN_PROGRESS",
    label: "In Progress",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        width="18"
        height="18"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5"
        />
      </svg>
    ),
    accent: "#3b82f6",
    accentBg: "#eff6ff",
    emptyMsg: "Nothing in progress",
  },

  {
    id: "COMPLETED",
    label: "Completed",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        width="18"
        height="18"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
        />
      </svg>
    ),
    accent: "#10b981",
    accentBg: "#ecfdf5",
    emptyMsg: "No completed tasks",
  },

  {
    id: "BLOCKED",
    label: "Blocked",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        width="18"
        height="18"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M6 18 18 6M6 6l12 12"
        />
      </svg>
    ),
    accent: "#ef4444",
    accentBg: "#fef2f2",
    emptyMsg: "Nothing blocked",
  },
];

const PRIORITY_STYLE = {
  high:   { bg: "#fef2f2", border: "#ef4444", text: "#991b1b" },
  medium: { bg: "#fffbeb", border: "#f59e0b", text: "#92400e" },
  low:    { bg: "#ecfdf5", border: "#10b981", text: "#065f46" },
};


export default function TaskBoard() {
  const { userProfile, isAdmin, isLead } = useAuth();
  const uid = userProfile?.uid;

  const { 
    allTasks, allTasksLoading,
    teamTasks, teamTasksLoading, subscribeToTeamTasks,
    userTasks, userTasksLoading, subscribeToUserTasks,
    subscribeToAllTasks,
    updateTaskStatus
  } = useTask();

  // Subscribe to appropriate tasks based on role
  useEffect(() => {
    if (isAdmin) {
      const unsub = subscribeToAllTasks();
      return unsub;
    } else if (isLead) {
      const unsub = subscribeToTeamTasks(uid);
      return unsub;
    } else {
      const unsub = subscribeToUserTasks(uid);
      return unsub;
    }
  }, [isAdmin, isLead, uid, subscribeToTeamTasks, subscribeToUserTasks, subscribeToAllTasks]);

  const tasks   = isAdmin ? allTasks   : isLead ? teamTasks : userTasks;
  const loading = isAdmin ? allTasksLoading : isLead ? teamTasksLoading : userTasksLoading;
  const nav     = isAdmin ? ADMIN_NAV  : isLead ? LEAD_NAV  : EMPLOYEE_NAV;
  const panel   = isAdmin ? "Admin Panel" : isLead ? "Team Lead Panel" : "My Portal";

  // Drag state
  const [draggedId,  setDraggedId]  = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);

  // Group tasks by status
  const grouped = useMemo(() => {
    const map = { PENDING: [], IN_PROGRESS: [], COMPLETED: [], BLOCKED: [] };
    for (const t of tasks) {
      const normalized = normalizeStatus(t.status);
      if (map[normalized]) {
        map[normalized].push(t);
      } else {
        map.PENDING.push(t);
      }
    }
    return map;
  }, [tasks]);

  // Summary counts
  const total    = tasks.length;
  const doneCount = grouped.COMPLETED.length;
  const pct = total > 0 ? Math.round((doneCount / total) * 100) : 0;

  function onDragStart(e, id) {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = "move";
  }

  function onDragEnd() {
    setDraggedId(null);
    setDragOverCol(null);
  }

  function onDragOver(e, colId) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverCol !== colId) setDragOverCol(colId);
  }

  async function onDrop(e, newStatus) {
    e.preventDefault();
    const id = draggedId;
    setDraggedId(null);
    setDragOverCol(null);
    if (!id) return;
    const task = tasks.find(t => t.id === id);
    if (!task || task.status === newStatus) return;
    
    try {
      await updateTaskStatus(id, newStatus);
    } catch (err) {
      console.error("[TaskBoard] update failed:", err);
      // The optimistic update will be rolled back automatically
    }
  }

  return (
    <Layout navItems={nav} panelLabel={panel}>
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title"> Task Board</h1>
        </div>
        {/* Progress bar */}
        <div style={{ alignSelf: "center", minWidth: 180 }}>
          <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginBottom: 4, textAlign: "right" }}>
            {pct}% complete
          </div>
          <div style={{ height: 8, background: "var(--border)", borderRadius: 99, overflow: "hidden" }}>
            <div style={{
              height: "100%",
              width: `${pct}%`,
              background: pct === 100 ? "var(--green)" : "var(--brand-accent)",
              borderRadius: 99,
              transition: "width 0.4s ease",
            }} />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="card-loading">Loading tasks…</div>
      ) : (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, minmax(200px, 1fr))",
          gap: "1rem",
          alignItems: "flex-start",
          overflowX: "auto",
          paddingBottom: "0.5rem",
        }}>
          {COLUMNS.map(col => {
            const colTasks  = grouped[col.id] || [];
            const isOver    = dragOverCol === col.id;

            return (
              <div
                key={col.id}
                onDragOver={e => onDragOver(e, col.id)}
                onDrop={e => onDrop(e, col.id)}
                onDragLeave={() => dragOverCol === col.id && setDragOverCol(null)}
                style={{
                  background: isOver ? col.accentBg : "var(--bg-input)",
                  border: `2px dashed ${isOver ? col.accent : "var(--border)"}`,
                  borderRadius: "var(--radius-lg)",
                  padding: "0.875rem",
                  minHeight: 320,
                  transition: "border-color 0.15s, background 0.15s",
                }}
              >
                {/* Column header */}
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "0.875rem",
                  paddingBottom: "0.625rem",
                  borderBottom: `2px solid ${col.accent}30`,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                    <span style={{ fontSize: 16 }}>{col.icon}</span>
                    <span style={{
                      fontWeight: 700,
                      fontSize: "0.875rem",
                      color: "var(--text-primary)",
                    }}>
                      {col.label}
                    </span>
                  </div>
                  <span style={{
                    background: col.accent + "20",
                    color: col.accent,
                    fontWeight: 700,
                    fontSize: "0.7rem",
                    padding: "0.15rem 0.5rem",
                    borderRadius: 99,
                    border: `1px solid ${col.accent}40`,
                    minWidth: 22,
                    textAlign: "center",
                  }}>
                    {colTasks.length}
                  </span>
                </div>

                {/* Cards */}
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                  {colTasks.length === 0 ? (
                    <div style={{
                      textAlign: "center",
                      padding: "2.5rem 1rem",
                      color: "var(--text-muted)",
                      fontSize: "0.78rem",
                      fontStyle: "italic",
                      border: "1px dashed var(--border)",
                      borderRadius: "var(--radius)",
                    }}>
                      {col.emptyMsg}
                    </div>
                  ) : (
                    colTasks.map(task => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        isDragging={draggedId === task.id}
                        onDragStart={onDragStart}
                        onDragEnd={onDragEnd}
                        columns={COLUMNS}
                        onQuickMove={async (id, status) => {
                          try { await updateTaskStatus(id, status); }
                          catch {}
                        }}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Layout>
  );
}

// ── Task card sub-component ──────────────────────────────
function TaskCard({ task, isDragging, onDragStart, onDragEnd, columns, onQuickMove }) {
  const [showMenu, setShowMenu] = useState(false);

  const deadline    = task.deadline?.toDate?.();
  const today       = new Date(); today.setHours(0,0,0,0);
  const isOverdue   = deadline && deadline < today && task.status !== "COMPLETED";
  const pStyle      = PRIORITY_STYLE[task.priority] || PRIORITY_STYLE.medium;

  return (
    <div
      draggable
      onDragStart={e => onDragStart(e, task.id)}
      onDragEnd={onDragEnd}
      style={{
        background: "var(--bg-card)",
        border: `1px solid ${pStyle.border}60`,
        borderLeft: `3px solid ${pStyle.border}`,
        borderRadius: "var(--radius)",
        padding: "0.75rem",
        cursor: "grab",
        opacity: isDragging ? 0.45 : 1,
        boxShadow: isDragging ? "var(--shadow-lg)" : "var(--shadow-sm)",
        transition: "opacity 0.15s, box-shadow 0.15s",
        userSelect: "none",
        position: "relative",
      }}
    >
      {/* Top row: title + menu */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: "0.375rem", marginBottom: "0.375rem" }}>
        <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
          {task.taskId && (
            <span style={{ fontSize: "0.7rem", color: "var(--text-muted)", fontWeight: 600 }}>
              {task.taskId}
            </span>
          )}
          <span style={{ fontWeight: 700, fontSize: "0.85rem", lineHeight: 1.35, color: "var(--text-primary)" }}>
            {task.title}
          </span>
        </div>
        <div style={{ position: "relative", flexShrink: 0 }}>
          <button
            title="Move to…"
            onClick={e => { e.stopPropagation(); setShowMenu(m => !m); }}
            style={{
              background: "none", border: "none",
              cursor: "pointer", fontSize: "1rem",
              color: "var(--text-muted)", padding: "0 2px",
              lineHeight: 1,
            }}
          >
            ···
          </button>
          {showMenu && (
            <div
              style={{
                position: "absolute", right: 0, top: "120%",
                background: "var(--bg-card)",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-sm)",
                boxShadow: "var(--shadow-lg)",
                zIndex: 50,
                minWidth: 140,
                overflow: "hidden",
              }}
            >
              <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "var(--text-muted)", padding: "0.4rem 0.65rem 0.25rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Move to
              </div>
              {columns.filter(c => c.id !== task.status).map(c => (
                <button
                  key={c.id}
                  onClick={async e => {
                    e.stopPropagation();
                    setShowMenu(false);
                    await onQuickMove(task.id, c.id);
                  }}
                  style={{
                    display: "flex", alignItems: "center", gap: "0.4rem",
                    width: "100%", padding: "0.45rem 0.65rem",
                    background: "none", border: "none",
                    cursor: "pointer", fontSize: "0.8rem",
                    color: "var(--text-primary)", textAlign: "left",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "var(--bg-input)"}
                  onMouseLeave={e => e.currentTarget.style.background = "none"}
                >
                  <span style={{ fontSize: 14 }}>{c.icon}</span> {c.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Description preview */}
      {task.description && (
        <p style={{
          fontSize: "0.73rem",
          color: "var(--text-secondary)",
          marginBottom: "0.4rem",
          lineHeight: 1.4,
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
        }}>
          {task.description}
        </p>
      )}

      {/* Meta row */}
      <div
  style={{
    display: "flex",
    flexDirection: "column",
    gap: "0.2rem",
    marginBottom: "0.5rem",
  }}
>
  <div style={{ fontSize: "0.65rem", color: "var(--text-muted)", marginBottom: "4px" }}>
    <strong>Created By:</strong> {task.assignedByName || "Admin"} on {task.createdAt?.toDate?.()?.toLocaleDateString?.("en-IN") || "N/A"}
  </div>

  {task.assignedToName && (
    <div
      style={{
        fontSize: "0.7rem",
        color: "var(--text-muted)",
        display: "flex",
        alignItems: "center",
        gap: "4px",
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        width="14"
        height="14"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
        />
      </svg>

      {task.assignedToName}
    </div>
  )}

  {deadline && (
    <div
      style={{
        fontSize: "0.7rem",
        color: isOverdue ? "var(--red)" : "var(--text-muted)",
        fontWeight: isOverdue ? 700 : 400,
        display: "flex",
        alignItems: "center",
        gap: "4px",
        flexWrap: "wrap",
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        width="14"
        height="14"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
        />
      </svg>

      {deadline.toLocaleDateString("en-IN")}

      {isOverdue && (
        <>
          <span>·</span>

          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            width="14"
            height="14"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
            />
          </svg>

          <span>Overdue</span>
        </>
      )}
    </div>
  )}
</div>

      {/* Footer: priority */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{
          fontSize: "0.65rem", fontWeight: 700,
          background: pStyle.bg, color: pStyle.text,
          padding: "0.15rem 0.5rem", borderRadius: 99,
          border: `1px solid ${pStyle.border}40`,
          textTransform: "capitalize",
        }}>
          {task.priority}
        </span>
      </div>

      {/* Click outside to close menu */}
      {showMenu && (
        <div
          onClick={() => setShowMenu(false)}
          style={{ position: "fixed", inset: 0, zIndex: 49 }}
        />
      )}
    </div>
  );
}